/*
 * Copyright (c) 2018-2023 MemberCenter, a subsidiary of Cameron Collective
 *   All Rights Reserved
 */
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Input from '@mui/material/Input';
import { MemberSignUpForm } from 'components';
import { propEq } from 'ramda';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
	useCreateMemberSignUpFormMutation,
	useEditMemberSignUpFormMutation,
	useGetMemberSignUpFormQuery
} from 'store/api/version-api.slice';
import { getParameterByName } from 'utils';

import NavigationArrows from '../components/NavigationArrows';
import { styles } from './styles/memberForm.styles';

const MemberForm = ({ handleNext, handlePrev, isNavigating }) => {
	const { data: memberSignUpFormResponse = {} } = useGetMemberSignUpFormQuery();
	const memberSignUpForm = useMemo(() => memberSignUpFormResponse.results || [], [memberSignUpFormResponse.results]);

	const [editMemberSignUpForm, { isLoading: isEditLoading }] = useEditMemberSignUpFormMutation();
	const [createMemberSignUpForm, { isLoading: isCreateLoading }] = useCreateMemberSignUpFormMutation();

	const [formOptions, setFormOptions] = useState([]);
	const [successUrl, setChangeSuccessUrl] = useState();
	const [memberInfoUrl, setChangeMemberInfoUrl] = useState();

	const handleChange = options => setFormOptions(options);

	useEffect(() => {
		if (memberSignUpForm.length > 0) {
			setFormOptions(memberSignUpForm[0].form_options);
		}
	}, [memberSignUpForm]);

	useEffect(() => {
		document.title = 'Member Center - Onboarding - Sign Up Form';

		window._hsq = window._hsq || [];
		const { _hsq } = window;

		_hsq.push(['setPath', '/onboarding-signup-form']);
		_hsq.push(['trackPageView']);
		_hsq.push([
			'identify',
			{
				email: getParameterByName('email'),
				onboarding_stage: 'signup_form'
			}
		]);
	}, []);

	const handleChangeSuccessUrl = e => {
		setChangeSuccessUrl(e.target.value);
	};

	const handleChangeMemberInfoUrl = e => {
		setChangeMemberInfoUrl(e.target.value);
	};

	const handleSave = useCallback(async () => {
		// Payload is an array of field values: ["emailAddress", "firstName"...]
		const payload = formOptions.filter(propEq(true, 'checked')).map(item => item.postValue);

		if (memberSignUpForm.length > 0) {
			try {
				await editMemberSignUpForm({ id: memberSignUpForm[0].id, payload, successUrl, memberInfoUrl });

				handleNext?.();
			} catch (e) {
				handleNext?.();
			}
		} else {
			try {
				await createMemberSignUpForm({ payload, successUrl, memberInfoUrl });

				handleNext?.();
			} catch (e) {
				handleNext?.();
			}
		}
	}, [
		createMemberSignUpForm,
		editMemberSignUpForm,
		formOptions,
		handleNext,
		memberInfoUrl,
		memberSignUpForm,
		successUrl
	]);

	const isNavigationDisabled = isEditLoading || isCreateLoading || isNavigating;

	return (
		<Grid sx={styles.actions}>
			<MemberSignUpForm options={formOptions} handleChange={handleChange} />
			<Box sx={styles.successWrapper}>
				<Box sx={styles.successContent}>
					<span>Success URL:</span>
					<Input type="text" value={successUrl} onChange={handleChangeSuccessUrl} defaultValue="https://" />
				</Box>
				<p>This is the webpage members will be sent to on successful form submission.</p>
			</Box>
			<Box sx={styles.successWrapper}>
				<Box sx={styles.successContent}>
					<span>Membership Information URL:</span>
					<Input
						type="text"
						value={memberInfoUrl}
						onChange={handleChangeMemberInfoUrl}
						defaultValue="https://"
					/>
				</Box>
				<p>
					This is the webpage potenial members will be sent to learn more about membership benefits. Currently
					only implemented for Gated Content.
				</p>
			</Box>
			<NavigationArrows
				handleNext={handleSave}
				handlePrev={handlePrev}
				className={styles.arrows}
				isNextDisabled={isNavigationDisabled}
				isBackDisabled={isNavigationDisabled}
				isNavigating={isNavigating}
			/>
		</Grid>
	);
};

export default MemberForm;
